import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import baseUrl from "../../../ajax/api";
import { pushGtmPageView } from "../../../utils/analytics";

// --- Legacy binary helpers (kept for backward compat with old base64 links) ---
const hexToBytes = (hex) => {
  const clean = hex.toLowerCase();
  const len = clean.length;
  const out = new Uint8Array(len >> 1);
  for (let i = 0; i < len; i += 2) {
    out[i >> 1] = parseInt(clean.substr(i, 2), 16);
  }
  return out;
};

const bytesToHex = (bytes) => {
  let s = "";
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i].toString(16).padStart(2, "0");
    s += b;
  }
  return s;
};

const writeVarUint = (num, arr) => {
  let n = Number(num >>> 0);
  while (n >= 0x80) {
    arr.push((n & 0x7f) | 0x80);
    n >>>= 7;
  }
  arr.push(n & 0x7f);
};

const readVarUint = (bytes, offset) => {
  let result = 0;
  let shift = 0;
  let pos = offset;
  while (pos < bytes.length) {
    const byte = bytes[pos++];
    result |= (byte & 0x7f) << shift;
    if ((byte & 0x80) === 0) break;
    shift += 7;
  }
  return { value: result >>> 0, next: pos };
};

const bytesToBase64Url = (bytes) => {
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const base64UrlToBytes = (str) => {
  try {
    if (!str || typeof str !== "string") {
      throw new Error("Invalid input: not a string");
    }
    const padLength = (4 - (str.length % 4)) % 4;
    const padded =
      str.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat(padLength);
    const bin = atob(padded);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch (error) {
    console.error("Base64 decode error:", error);
    throw new Error(`Failed to decode base64: ${error.message}`);
  }
};

// --- Legacy pack/unpack (kept for backward compat with old base64 links) ---
const packPayload = ({ jobId, refCode, companyId, isInvite }) => {
  const out = [];
  out.push(1);
  const hasCompany = companyId != null && companyId !== "";
  let flags = 0;
  if (hasCompany) flags |= 0x01;
  if (isInvite) flags |= 0x02;
  out.push(flags);
  writeVarUint(Number(jobId), out);
  if (hasCompany) writeVarUint(Number(companyId), out);
  let ref = String(refCode || "");
  if (ref.startsWith("WBREF_")) ref = ref.slice(6);
  let refBytes;
  if (/^[0-9a-fA-F]+$/.test(ref) && ref.length % 2 === 0) {
    refBytes = hexToBytes(ref);
  } else {
    refBytes = new TextEncoder().encode(ref);
  }
  writeVarUint(refBytes.length, out);
  for (let i = 0; i < refBytes.length; i++) out.push(refBytes[i]);
  return new Uint8Array(out);
};

const unpackPayload = (bytes) => {
  let pos = 0;
  if (bytes.length < 2) return null;
  const version = bytes[pos++];
  if (version !== 1) return null;
  const flags = bytes[pos++];
  const hasCompany = (flags & 0x01) === 1;
  const isInvite = (flags & 0x02) === 2;
  const j = readVarUint(bytes, pos);
  const jobId = j.value;
  pos = j.next;
  let companyId = undefined;
  if (hasCompany) {
    const c = readVarUint(bytes, pos);
    companyId = c.value;
    pos = c.next;
  }
  const rl = readVarUint(bytes, pos);
  const refLen = rl.value;
  pos = rl.next;
  if (pos + refLen > bytes.length) return null;
  const refBytes = bytes.slice(pos, pos + refLen);
  const refHex = bytesToHex(refBytes);
  return { jobId, companyId, refHex, isInvite };
};

// --- New human-readable format helpers ---
// Format: username{userId}-{jobId}  e.g. godfrey892-537
// With company: username{userId}-{jobId}?c={companyId}
const slugifyUsername = (username) => {
  return String(username || "user")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .substring(0, 20);
};

const parseHumanReadableCode = (code) => {
  // Match: letters+digits ending with digits, dash, digits
  // e.g. godfrey892-537
  const match = code.match(/^([a-z0-9]+?)(\d+)-(\d+)$/i);
  if (!match) return null;
  const username = match[1];
  const userId = parseInt(match[2], 10);
  const jobId = parseInt(match[3], 10);
  if (!userId || !jobId || isNaN(userId) || isNaN(jobId)) return null;
  return { username, userId, jobId };
};

const isLegacyBase64 = (code) => {
  // Old format was base64url — no dash in the middle with digits on both sides
  // New format has pattern: word+digits-digits
  return !parseHumanReadableCode(code);
};

const fetchRefCode = async (userId) => {
  const res = await axios.get(`${baseUrl}/user/${userId}/refcode`);
  if (res.data && res.data.status === true && res.data.data && res.data.data.refCode) {
    return res.data.data.refCode;
  }
  throw new Error("Could not resolve refCode");
};

const ShortRedirect = () => {
  const { code } = useParams();
  const history = useHistory();
  const { isAuthenticated } = useSelector((store) => store.auth);
  const [status, setStatus] = useState("Redirecting…");

  useEffect(() => {
    if (!code) {
      window.location.href = "/";
      return;
    }

    pushGtmPageView({
      page_path: `/r/${code}`,
      page_location: `${window.location.origin}/r/${code}${window.location.search}`,
    });

    const handleLegacyBase64 = (code) => {
      try {
        const bytes = base64UrlToBytes(code);
        const unpacked = unpackPayload(bytes);

        if (!unpacked) {
          console.error("Failed to unpack payload");
          window.location.href = "/";
          return;
        }

        const { jobId, companyId, refHex, isInvite } = unpacked;

        if (!jobId || isNaN(jobId) || jobId <= 0) {
          console.error("Invalid jobId:", jobId);
          window.location.href = "/";
          return;
        }

        const refCode = isInvite ? 'invite' : `WBREF_${refHex}`;
        doRedirect(jobId, companyId, refCode, isInvite);
      } catch (error) {
        console.error("Legacy redirect error:", error);
        window.location.href = "/";
      }
    };

    const handleHumanReadable = async (code) => {
      const parsed = parseHumanReadableCode(code);
      if (!parsed) {
        console.error("Failed to parse code:", code);
        window.location.href = "/";
        return;
      }

      const { userId, jobId } = parsed;

      // Extract companyId from query string if present
      const urlParams = new URLSearchParams(window.location.search);
      const companyId = urlParams.get("c") || undefined;

      // Fetch the refCode from the API
      try {
        setStatus("Resolving referral…");
        const refCode = await fetchRefCode(userId);
        doRedirect(jobId, companyId, refCode, false);
      } catch (error) {
        console.error("Failed to resolve refCode:", error);
        // Fallback: redirect without refCode so user still sees the job
        doRedirect(jobId, companyId, null, false);
      }
    };

    const doRedirect = (jobId, companyId, refCode, isInvite) => {
      const basePath = isAuthenticated ? "/home" : "/job-lists-details";
      const refPath = refCode ? `/${encodeURIComponent(refCode)}` : "";
      const companyPath = companyId ? `/coy/${companyId}` : "";
      const inviteQuery = isInvite ? "?source=invite" : "";
      const targetPath = `${basePath}/${jobId}/apply${refPath}${companyPath}${inviteQuery}`;

      // So login/signup can send the user to the apply flow
      const postLoginApplyPath = `/home/${jobId}/apply${refPath}${companyPath}`;
      const postLoginPathWithQuery = isInvite
        ? `${postLoginApplyPath}?source=invite`
        : postLoginApplyPath;
      if (!isAuthenticated) {
        sessionStorage.setItem(
          "pageToVisit",
          JSON.stringify({
            accountType: localStorage.getItem("accountType"),
            path: postLoginPathWithQuery,
          }),
        );
      }

      const navigateToTarget = () => {
        pushGtmPageView({
          page_path: targetPath.split("?")[0],
          page_location: `${window.location.origin}${targetPath}`,
        });
        history.replace(targetPath);
      };

      // Detect mobile device
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile) {
        const appPath = `job-lists-details/${jobId}/apply${refPath}${companyPath}`;
        const appLink = `workbrook://${appPath}`;

        let didLeavePage = false;
        const onVisibilityChange = () => {
          if (document.hidden) didLeavePage = true;
        };
        document.addEventListener("visibilitychange", onVisibilityChange);

        window.location.href = appLink;

        setTimeout(() => {
          document.removeEventListener("visibilitychange", onVisibilityChange);
          if (!didLeavePage) {
            navigateToTarget();
          }
        }, 2500);
      } else {
        navigateToTarget();
      }
    };

    // Check if this is a legacy base64 link or new human-readable format
    if (isLegacyBase64(code)) {
      handleLegacyBase64(code);
    } else {
      handleHumanReadable(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div
      className="w-100 d-flex align-items-center justify-content-center"
      style={{ minHeight: 200 }}
    >
      <span>{status}</span>
    </div>
  );
};

export default ShortRedirect;

// New human-readable format: username{userId}-{jobId}
// With company: appends ?c={companyId} as query param
export const encodeShortPayload = ({ jobId, username, userId, companyId, isInvite }) => {
  // If username/userId not provided, fall back to legacy base64 format
  if (!username || !userId) {
    const bytes = packPayload({ jobId, refCode: username, companyId, isInvite });
    return bytesToBase64Url(bytes);
  }

  const slug = slugifyUsername(username);
  const code = `${slug}${userId}-${jobId}`;

  if (companyId != null && companyId !== "") {
    return `${code}?c=${companyId}`;
  }
  return code;
};

// Legacy export for any code still using the old signature
export const encodeShortPayloadLegacy = ({ jobId, refCode, companyId, isInvite }) => {
  const bytes = packPayload({ jobId, refCode, companyId, isInvite });
  return bytesToBase64Url(bytes);
};
